import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Col,
  Dropdown,
  Form,
  Modal,
  Row,
  Table,
} from "react-bootstrap";
import { BsCheckCircle, BsThreeDots } from "react-icons/bs";
import { FiEdit, FiEye, FiTrash2 } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import CreatableSelect from "react-select/creatable";

import styles from "./sections.module.scss";
import {
  addOrUpdateReport,
  deleteReportSlice,
  getPatientIdSlice,
} from "../../../../store/slices/patientSlice";
import {
  generateLinkToken,
  initiateLinking,
} from "../../../../store/slices/shareAbhaDataSlice";
import { getServiceSlice } from "../../../../store/slices/serviceSlice";
import { getAllPhysician, getPhysicianByClinicId } from "../../../../store/slices/physicianSlice";
import { getMedicineSlice } from "../../../../store/slices/medicineMasterSlice";

const DEFAULT_MEDICATION_INSTRUCTIONS = [
  "Once daily, Before food",
  "Once daily, After food",
  "Once daily, At bedtime",
  "Twice daily, Before food",
  "Twice daily, After food",
  "Morning & Evening",
  "Thrice daily, After food",
  "Morning, Afternoon & Night",
  "Once weekly",
  "Alternate days",
  "As needed (SOS)",
  "When required for pain",
  "When fever > 100°F",
  "Before breakfast",
  "After meals",
  "Empty stomach",
  "For 3 days",
  "For 5 days",
  "For 7 days",
  "Continue till advised",
  "Custom, As per doctor's advice",
];

function buildInitialFormData(config) {
  const base = {
    _id: "",
    consultationDate: "",
    service: "",
    doctor: "",
    patient_report: [],
    status: "Unshared",
    medicines: config.includeMedicines ? [] : undefined,
  };

  (config.fields || []).forEach((field) => {
    base[field.key] = field.defaultValue ?? "";
  });

  return base;
}

function getDefaultAuthorMode(config) {
  if (config.authorModeDefault) return config.authorModeDefault;
  return config.requireDoctor ? "clinician" : "organization";
}

function formatDate(value) {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-GB");
}

function hasValue(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

function convertJpgToPng(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("PNG conversion failed"));
              return;
            }

            resolve(
              new File([blob], file.name.replace(/\.(jpg|jpeg)$/i, ".png"), {
                type: "image/png",
              }),
            );
          },
          "image/png",
          1.0,
        );
      };

      img.src = event.target.result;
    };

    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const ClinicalReportSection = ({
  patientId,
  patientData,
  config,
  externalRows = [],
  externalLoading = false,
  onExternalView,
}) => {
  const dispatch = useDispatch();

  const { clinicInfo } = useSelector((state) => state.clinic);
  const { token } = useSelector((state) => state.auth);
  const { loading } = useSelector((state) => state.patient);
  const { services } = useSelector((state) => state.service);
  const { physician, getPhysicianDetailsByClinic } = useSelector((state) => state.physician);
  const { medicine } = useSelector((state) => state.medicineMaster);

  const clinicId = clinicInfo?.[0]?.clinicId || null;
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState(() => buildInitialFormData(config));
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState("");
  const [showError, setShowError] = useState("");
  const [medicineInput, setMedicineInput] = useState(null);
  const [medicineErrMsg, setMedicineErrMsg] = useState("");
  const [medicineInstructionErrMsg, setMedicineInstructionErrMsg] = useState([]);
  const [selectedAuthorMode, setSelectedAuthorMode] = useState(() =>
    getDefaultAuthorMode(config),
  );

  const [showModal, setShowModal] = useState(false);
  const [modalAction, setModalAction] = useState("create");

  const [selectedItem, setSelectedItem] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);

  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedReports, setSelectedReports] = useState([]);
  const [isSharing, setIsSharing] = useState(false);
  const [isFinalSharing, setIsFinalSharing] = useState(false);

  const [showInvalidTokenModal, setShowInvalidTokenModal] = useState(false);
  const [showLinkedSuccessModal, setShowLinkedSuccessModal] = useState(false);
  const [showLinkedErrorModal, setShowLinkedErrorModal] = useState(false);

  useEffect(() => {
    if (token) {
      if (config.includeService) dispatch(getServiceSlice({ token }));
      if (config.includeDoctor && clinicId) {
        dispatch(getPhysicianByClinicId({ token, clinicId }));
      } else if (config.includeDoctor) {
        dispatch(getAllPhysician({ token }));
      }
      if (config.includeMedicines) dispatch(getMedicineSlice({ token }));
    }
  }, [
    dispatch,
    token,
    clinicId,
    config.includeService,
    config.includeDoctor,
    config.includeMedicines,
  ]);

  useEffect(() => {
    setSelectedAuthorMode(getDefaultAuthorMode(config));
  }, [config.authorModeDefault, config.requireDoctor]);

  const reports = useMemo(() => {
    if (!Array.isArray(patientData?.reportDetails)) return [];

    return patientData.reportDetails
      .filter(
        (item) =>
          item?.reportType === config.reportType &&
          String(item?.clinicId) === String(clinicId),
      )
      .sort(
        (a, b) =>
          new Date(b?.consultationDate || b?.createdAt || 0) -
          new Date(a?.consultationDate || a?.createdAt || 0),
      );
  }, [patientData, clinicId, config.reportType]);

  const mergedRows = useMemo(() => {
    const baseRows = reports.map((item) => ({
      ...item,
      __rowSource: "abha",
    }));

    const mappedExternal = (Array.isArray(externalRows) ? externalRows : []).map(
      (item) => ({
        ...item,
        __rowSource: "external",
      }),
    );

    return [...mappedExternal, ...baseRows].sort(
      (a, b) =>
        new Date(b?.consultationDate || b?.createdAt || 0) -
        new Date(a?.consultationDate || a?.createdAt || 0),
    );
  }, [reports, externalRows]);

  const medicineOptions = useMemo(() => {
    return (medicine || [])
      .map((item) => {
        const label = (item?.genericName || item?.brandName || "").trim();
        if (!label) return null;
        return {
          label,
          value: label,
        };
      })
      .filter(Boolean);
  }, [medicine]);

  const medicationInstructionOptions = useMemo(() => {
    const rawOptions =
      Array.isArray(config.medicineInstructionOptions) &&
      config.medicineInstructionOptions.length > 0
        ? config.medicineInstructionOptions
        : DEFAULT_MEDICATION_INSTRUCTIONS;

    return rawOptions
      .map((item) => {
        if (typeof item === "string") {
          const value = item.trim();
          return value ? { label: value, value } : null;
        }
        if (item && typeof item === "object") {
          const value = String(item.value || item.label || "").trim();
          if (!value) return null;
          return {
            label: String(item.label || value),
            value,
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [config.medicineInstructionOptions]);

  const getDoctorNameById = (doctorId) => {
    if (!doctorId) return "-";
    const doc = doctorOptions.find((item) => String(item?._id) === String(doctorId));
    return doc?.userId?.name || "-";
  };

  const doctorOptions = useMemo(() => {
    const clinicDoctors = Array.isArray(getPhysicianDetailsByClinic)
      ? getPhysicianDetailsByClinic
      : [];
    const allDoctors = Array.isArray(physician) ? physician : [];
    return clinicDoctors.length > 0 ? clinicDoctors : allDoctors;
  }, [getPhysicianDetailsByClinic, physician]);

  const getServiceNameById = (service) => {
    if (!service) return "-";
    if (typeof service === "object" && service.serviceName) return service.serviceName;
    const found = services?.find((item) => String(item?._id) === String(service));
    return found?.serviceName || "-";
  };

  const normalizeDateForInput = (value) => {
    if (!value) return "";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toISOString().split("T")[0];
  };

  const showServiceColumn = config.includeService || Boolean(config.tableServiceLabel);
  const showDoctorColumn = config.includeDoctor || Boolean(config.tableDoctorLabel);
  const showSummaryColumn = Boolean(config.summaryColumn?.label);
  const dateLabel = config.dateLabel || "Consultation Date";
  const servicePlaceholder = config.servicePlaceholder || "Select Service";
  const doctorPlaceholder = config.doctorPlaceholder || "Select Doctor";
  const doctorFallbackLabel = config.doctorFallbackLabel || "-";
  const emptyValueText = config.viewEmptyValueText || "Not documented";
  const authorModeOptions = Array.isArray(config.authorModeOptions)
    ? config.authorModeOptions
    : [];
  const hasAuthorModeSelector = config.includeDoctor && authorModeOptions.length > 1;
  const doctorModeValue = config.authorModeDoctorValue || "clinician";
  const requiresDoctorByAuthorMode =
    !hasAuthorModeSelector || selectedAuthorMode === doctorModeValue;

  const tableColumns = useMemo(() => {
    if (Array.isArray(config.tableColumns) && config.tableColumns.length > 0) {
      return config.tableColumns;
    }

    const columns = [
      {
        key: "consultationDate",
        label: "Consultation Date",
        render: (item, helpers) => helpers.formatDate(item?.consultationDate),
      },
    ];

    if (showServiceColumn) {
      columns.push({
        key: "service",
        label: config.tableServiceLabel || "Service",
        render: (item, helpers) =>
          config.includeService
            ? helpers.getServiceNameById(item?.service)
            : item?.serviceName || "-",
      });
    }

    if (showDoctorColumn) {
      columns.push({
        key: "doctor",
        label: config.tableDoctorLabel || "Doctor",
        render: (item, helpers) =>
          config.includeDoctor
            ? (helpers.getDoctorNameById(item?.doctor) === "-"
                ? doctorFallbackLabel
                : helpers.getDoctorNameById(item?.doctor))
            : item?.doctorName || "-",
      });
    }

    if (showSummaryColumn) {
      columns.push({
        key: "summary",
        label: config.summaryColumn.label,
        render: (item, helpers) =>
          typeof config.summaryColumn.render === "function"
            ? config.summaryColumn.render(item, helpers)
            : "-",
      });
    }

    return columns;
  }, [
    config,
    showServiceColumn,
    showDoctorColumn,
    showSummaryColumn,
    doctorFallbackLabel,
  ]);

  const tableColumnCount = tableColumns.length + 3;
  const fieldConfigMap = useMemo(() => {
    const map = new Map();
    (config.fields || []).forEach((field) => {
      map.set(field.key, field);
    });
    return map;
  }, [config.fields]);

  const resetFormState = () => {
    setFormData(buildInitialFormData(config));
    setSelectedAuthorMode(getDefaultAuthorMode(config));
    setMedicineInput(null);
    setMedicineErrMsg("");
    setMedicineInstructionErrMsg([]);
    setFiles([]);
    setFileError("");
    setShowError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleAddClick = () => {
    setModalAction("create");
    resetFormState();
    setShowModal(true);
  };

  const handleEditClick = (report) => {
    setModalAction("edit");
    setFiles([]);
    setFileError("");
    setShowError("");
    setMedicineInput(null);
    setMedicineErrMsg("");
    setMedicineInstructionErrMsg([]);

    const next = buildInitialFormData(config);
    next._id = report?._id || "";
    next.consultationDate = report?.consultationDate
      ? report.consultationDate.split("T")[0]
      : "";
    next.service = report?.service?._id || report?.service || "";
    next.doctor = report?.doctor || "";
    next.patient_report = Array.isArray(report?.patient_report)
      ? report.patient_report
      : report?.patient_report
        ? [report.patient_report]
        : [];
    next.status = report?.status || "Unshared";

    if (config.includeMedicines) {
      next.medicines = Array.isArray(report?.medicines)
        ? report.medicines
        : [];
    }

    (config.fields || []).forEach((field) => {
      next[field.key] = report?.[field.key] ?? field.defaultValue ?? "";
    });

    setFormData(next);
    setSelectedAuthorMode(
      report?.doctor
        ? doctorModeValue
        : config.authorModeFallback || getDefaultAuthorMode(config),
    );

    if (fileInputRef.current) fileInputRef.current.value = "";
    setShowModal(true);
  };

  const handleImportExternalRow = (externalRow) => {
    setModalAction("create");
    setFiles([]);
    setFileError("");
    setShowError("");
    setMedicineInput(null);
    setMedicineErrMsg("");
    setMedicineInstructionErrMsg([]);

    const next = buildInitialFormData(config);
    next.consultationDate = normalizeDateForInput(externalRow?.consultationDate);

    if (config.includeService) {
      next.service = externalRow?.service || "";
    }
    if (config.includeDoctor) {
      next.doctor = externalRow?.doctor || "";
    }

    if (config.includeMedicines && Array.isArray(externalRow?.medicines)) {
      next.medicines = externalRow.medicines
        .map((item) => ({
          name: String(item?.name || "").trim(),
          instruction: String(item?.instruction || "").trim(),
        }))
        .filter(
          (item) => item.name.length > 0 || item.instruction.length > 0,
        );
    }

    if (config.enableFileUpload !== false) {
      next.patient_report = Array.isArray(externalRow?.patient_report)
        ? externalRow.patient_report.filter(Boolean)
        : [];
    }

    (config.fields || []).forEach((field) => {
      const value = externalRow?.[field.key];
      if (value !== undefined && value !== null && value !== "") {
        next[field.key] = value;
      }
    });

    setFormData(next);
    setSelectedAuthorMode(
      externalRow?.doctor
        ? doctorModeValue
        : config.authorModeFallback || getDefaultAuthorMode(config),
    );
    if (fileInputRef.current) fileInputRef.current.value = "";
    setShowModal(true);
  };

  const getStatusMeta = (item) => {
    if (item?.__rowSource === "external") {
      return {
        label: item?.sourceStatusText || "Generated",
        className: styles.Unshared,
      };
    }

    if (item?.status === "Shared") {
      return { label: "Linked", className: styles.Shared };
    }
    if (item?.status === "Transferred") {
      return { label: "Transferred", className: styles.Transferred };
    }
    return { label: "Not Linked", className: styles.Unshared };
  };

  const handleModalClose = () => {
    setShowModal(false);
    resetFormState();
  };

  const handleFileChange = (event) => {
    setFileError("");
    setShowError("");

    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length === 0) return;

    const validFiles = selectedFiles.filter((file) => file.size <= 1024 * 1024);
    const oversizedFiles = selectedFiles.filter((file) => file.size > 1024 * 1024);

    if (oversizedFiles.length > 0) {
      setFileError("Each file must be less than or equal to 1MB.");
    }

    if (validFiles.length === 0) {
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    const newIsAllImages = validFiles.every((file) =>
      file.type.startsWith("image/"),
    );
    const newIsAllPDFs = validFiles.every(
      (file) => file.type === "application/pdf",
    );

    const currentIsAllImages = files.every((file) =>
      file.type.startsWith("image/"),
    );
    const currentIsAllPDFs = files.every(
      (file) => file.type === "application/pdf",
    );

    const newType = newIsAllImages ? "image" : newIsAllPDFs ? "pdf" : null;
    const currentType = currentIsAllImages
      ? "image"
      : currentIsAllPDFs
        ? "pdf"
        : null;

    if (!newType) {
      setFileError("Please select only images OR only PDFs, not both.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    if (files.length > 0 && newType !== currentType) {
      setFileError(
        "You can only select one type of file. Mixing formats is not allowed.",
      );
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setFiles((prev) => [...prev, ...validFiles]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleMedicineChange = (index, key, value) => {
    setFormData((prev) => {
      const list = Array.isArray(prev.medicines) ? [...prev.medicines] : [];
      list[index] = {
        ...list[index],
        [key]: value,
      };
      return {
        ...prev,
        medicines: list,
      };
    });
  };

  const addMedicine = (value) => {
    if (!String(value || "").trim()) return;
    setFormData((prev) => ({
      ...prev,
      medicines: [
        ...(prev.medicines || []),
        { name: String(value).trim(), instruction: "" },
      ],
    }));
    setMedicineInput(null);
    setMedicineErrMsg("");
  };

  const removeMedicineRow = (index) => {
    setMedicineErrMsg("");
    setFormData((prev) => ({
      ...prev,
      medicines: (prev.medicines || []).filter((_, itemIndex) => itemIndex !== index),
    }));
    setMedicineInstructionErrMsg((prev) =>
      Array.isArray(prev) ? prev.filter((_, itemIndex) => itemIndex !== index) : [],
    );
  };

  const validateForm = () => {
    if (config.requireDate && !formData.consultationDate) {
      setShowError(`${dateLabel} is required.`);
      return false;
    }

    if (config.requireService && !hasValue(formData.service)) {
      setShowError(config.serviceRequiredMessage || "Service is required.");
      return false;
    }

    if (config.requireDoctor && !hasValue(formData.doctor)) {
      setShowError(config.doctorRequiredMessage || "Doctor is required.");
      return false;
    }

    if (
      !config.requireDoctor &&
      config.includeDoctor &&
      requiresDoctorByAuthorMode &&
      !hasValue(formData.doctor)
    ) {
      setShowError(
        config.doctorRequiredMessage ||
          `${config.doctorLabel || "Doctor"} is required for the selected author source.`,
      );
      return false;
    }

    const requiredField = (config.fields || []).find(
      (field) => field.required && !hasValue(formData[field.key]),
    );
    if (requiredField) {
      setShowError(requiredField.requiredMessage || `${requiredField.label} is required.`);
      return false;
    }

    if (config.includeMedicines) {
      setMedicineErrMsg("");
      setMedicineInstructionErrMsg([]);

      const medicines = formData.medicines || [];
      const medicinesAreOptional = config.medicinesOptional === true;
      const minimum = Number(config.minimumMedicines || 0);
      if (minimum > 0 && medicines.length < minimum) {
        const msg =
          config.medicinesRequiredMessage ||
          `Please add at least ${minimum} medicine${minimum > 1 ? "s" : ""}.`;
        setMedicineErrMsg(msg);
        setShowError(msg);
        return false;
      }

      if (!medicinesAreOptional && medicines.length === 0) {
        const msg = config.medicinesRequiredMessage || "Add at least one medicine.";
        setMedicineErrMsg(msg);
        setShowError(msg);
        return false;
      }

      const hasNameError = medicines.some(
        (med) => !String(med?.name || "").trim(),
      );
      if (hasNameError) {
        const msg = "Each medicine entry requires a medicine name.";
        setMedicineErrMsg(msg);
        setShowError(msg);
        return false;
      }

      const instructionErrors = [];
      medicines.forEach((med, index) => {
        if (!String(med?.instruction || "").trim()) {
          instructionErrors[index] = "Instruction is required";
        }
      });

      if (instructionErrors.length > 0) {
        const msg = "Please provide instruction for each added medicine.";
        setMedicineInstructionErrMsg(instructionErrors);
        setMedicineErrMsg(msg);
        setShowError(msg);
        return false;
      }
    }

    if (config.enableFileUpload !== false) {
      const totalFiles =
        files.length + (Array.isArray(formData.patient_report) ? formData.patient_report.length : 0);

      if (config.fileRequired && totalFiles === 0) {
        setShowError(`Please upload ${config.fileLabel || "report files"}.`);
        return false;
      }

      if (config.fileRequiredOnCreate && modalAction !== "edit" && files.length === 0) {
        setShowError(`Please upload ${config.fileLabel || "report files"}.`);
        return false;
      }
    }

    return true;
  };

  const buildReportPayload = () => {
    const payload = {
      ...(formData._id ? { _id: formData._id } : {}),
      reportType: config.reportType,
      consultationDate: formData.consultationDate || new Date().toISOString(),
      status: formData.status || "Unshared",
      patient_report:
        config.enableFileUpload === false ? [] : formData.patient_report || [],
    };

    if (config.includeService && formData.service) {
      payload.service = formData.service;
      const serviceName = getServiceNameById(formData.service);
      if (serviceName && serviceName !== "-") payload.serviceName = serviceName;
    }
    if (config.includeDoctor && formData.doctor) payload.doctor = formData.doctor;

    if (config.includeMedicines) {
      payload.medicines = formData.medicines || [];
    }

    (config.fields || []).forEach((field) => {
      const value = formData[field.key];
      if (value !== undefined) payload[field.key] = value;
    });

    return payload;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setShowError("");
    setMedicineErrMsg("");
    setMedicineInstructionErrMsg([]);

    if (!patientId || !clinicId) {
      setShowError("Patient or clinic information is missing.");
      return;
    }

    if (!validateForm()) return;

    const reportData = buildReportPayload();
    const formDataToSend = new FormData();

    formDataToSend.append("patientId", patientId);
    formDataToSend.append("clinicId", clinicId);
    formDataToSend.append("report", JSON.stringify(reportData));

    if (config.enableFileUpload !== false) {
      const convertedFiles = await Promise.all(
        files.map(async (file) => {
          if (/\.(jpe?g)$/i.test(file.name)) return convertJpgToPng(file);
          return file;
        }),
      );

      convertedFiles.forEach((file) => formDataToSend.append("file", file));
    }

    try {
      await dispatch(addOrUpdateReport({ formDataToSend, token })).unwrap();
      await dispatch(getPatientIdSlice({ token, patientId })).unwrap();
      handleModalClose();
    } catch (error) {
      setShowError(error || "Unable to save report.");
    }
  };

  const handleDeleteReport = async (reportId) => {
    try {
      await dispatch(
        deleteReportSlice({
          patientId,
          reportId,
          token,
        }),
      ).unwrap();
      await dispatch(getPatientIdSlice({ token, patientId })).unwrap();
    } catch (error) {
      setShowLinkedErrorModal(true);
    }
  };

  const handleShareClick = async () => {
    try {
      setIsSharing(true);

      const response = await dispatch(
        generateLinkToken({
          clinicId,
          userId: patientData?.userId?._id,
          token,
        }),
      );

      if (response?.payload?.success) {
        setSelectedReports([]);
        setShowShareModal(true);
      } else {
        setShowInvalidTokenModal(true);
      }
    } catch (error) {
      setShowInvalidTokenModal(true);
    } finally {
      setIsSharing(false);
    }
  };

  const handleCheckboxChange = (id, disabled) => {
    if (disabled) return;

    setSelectedReports((prev) =>
      prev.includes(id) ? prev.filter((reportId) => reportId !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    const selectable = reports
      .filter((report) => !["Shared", "Transferred"].includes(report.status))
      .map((report) => report._id);

    const allSelected =
      selectable.length > 0 &&
      selectable.every((reportId) => selectedReports.includes(reportId));

    if (allSelected) setSelectedReports([]);
    else setSelectedReports(selectable);
  };

  const buildCareContextDisplay = (report) => {
    if (typeof config.buildDisplay === "function") {
      return config.buildDisplay(report, { getServiceNameById, getDoctorNameById });
    }

    return `${config.title} - ${formatDate(report?.consultationDate)}`;
  };

  const handleFinalShare = async () => {
    const reportsToShare = selectedReports
      .map((id) => reports.find((item) => item._id === id))
      .filter(
        (item) => item && !["Shared", "Transferred"].includes(item.status),
      );

    if (reportsToShare.length === 0) return;

    const careContextData = reportsToShare.map((report) => ({
      referenceNumber: `${config.prefix}-${report._id}`,
      display: buildCareContextDisplay(report),
    }));

    try {
      setIsFinalSharing(true);

      const requestBody = {
        patientId,
        hiTypes: config.hiType,
        clinicId,
        careContextData,
      };

      const response = await dispatch(
        initiateLinking({ ...requestBody, token }),
      ).unwrap();

      if (!response?.success) {
        throw new Error(response?.message || "Linking failed");
      }

      await dispatch(getPatientIdSlice({ token, patientId })).unwrap();
      setShowLinkedSuccessModal(true);
      setSelectedReports([]);
    } catch (error) {
      setShowLinkedErrorModal(true);
    } finally {
      setIsFinalSharing(false);
      setShowShareModal(false);
    }
  };

  const renderDynamicField = (field) => {
    const value = formData[field.key] ?? "";

    if (field.type === "textarea") {
      return (
        <Form.Control
          as="textarea"
          rows={field.rows || 3}
          maxLength={field.maxLength}
          value={value}
          onChange={(event) =>
            setFormData((prev) => ({
              ...prev,
              [field.key]: event.target.value,
            }))
          }
          placeholder={field.placeholder || field.label}
        />
      );
    }

    if (field.type === "number") {
      return (
        <Form.Control
          type="number"
          value={value}
          min={field.min}
          max={field.max}
          step={field.step}
          onChange={(event) =>
            setFormData((prev) => ({
              ...prev,
              [field.key]: event.target.value,
            }))
          }
          placeholder={field.placeholder || field.label}
        />
      );
    }

    if (field.type === "date") {
      return (
        <Form.Control
          type="date"
          value={value}
          max={
            field.disableMax
              ? undefined
              : field.max || new Date().toISOString().split("T")[0]
          }
          onChange={(event) =>
            setFormData((prev) => ({
              ...prev,
              [field.key]: event.target.value,
            }))
          }
        />
      );
    }

    if (field.type === "select") {
      return (
        <Form.Select
          value={value}
          onChange={(event) =>
            setFormData((prev) => ({
              ...prev,
              [field.key]: event.target.value,
            }))
          }
        >
          <option value="">{field.placeholder || `Select ${field.label}`}</option>
          {(field.options || []).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Form.Select>
      );
    }

    return (
      <Form.Control
        type="text"
        maxLength={field.maxLength}
        value={value}
        onChange={(event) =>
          setFormData((prev) => ({
            ...prev,
            [field.key]: event.target.value,
          }))
        }
        placeholder={field.placeholder || field.label}
      />
    );
  };

  const renderConfiguredFieldBlocks = () => {
    const configuredSections = Array.isArray(config.formSections)
      ? config.formSections
      : [];

    if (configuredSections.length === 0) {
      return (config.fields || []).map((field) => (
        <Form.Group className="mb-3" key={field.key}>
          <Form.Label>
            {field.label} {field.required ? <span className="text-danger">*</span> : null}
          </Form.Label>
          {renderDynamicField(field)}
          {field.helpText ? (
            <Form.Text className={styles.fieldHelpText}>{field.helpText}</Form.Text>
          ) : null}
        </Form.Group>
      ));
    }

    return configuredSections.map((section, sectionIndex) => {
      const sectionFields = (section.fieldKeys || [])
        .map((fieldKey) => fieldConfigMap.get(fieldKey))
        .filter(Boolean);

      if (sectionFields.length === 0) return null;

      return (
        <div
          key={`form-section-${section.title || sectionIndex}`}
          className={styles.formSectionCard}
        >
          <div className={styles.formSectionTitle}>{section.title || "Section"}</div>
          {section.description ? (
            <div className={styles.formSectionDescription}>{section.description}</div>
          ) : null}
          {sectionFields.map((field) => (
            <Form.Group className="mb-3" key={field.key}>
              <Form.Label>
                {field.label} {field.required ? <span className="text-danger">*</span> : null}
              </Form.Label>
              {renderDynamicField(field)}
              {field.helpText ? (
                <Form.Text className={styles.fieldHelpText}>{field.helpText}</Form.Text>
              ) : null}
            </Form.Group>
          ))}
        </div>
      );
    });
  };

  const renderViewValue = (value) => {
    if (value === null || value === undefined || value === "") return emptyValueText;
    if (Array.isArray(value)) return value.length > 0 ? value.join(", ") : emptyValueText;
    return String(value);
  };

  const renderDetailRow = (label, value, rowKey) => (
    <div className={styles.viewFieldRow} key={rowKey}>
      <div className={styles.viewFieldLabel}>{label}</div>
      <div className={styles.viewFieldValue}>{renderViewValue(value)}</div>
    </div>
  );

  return (
    <div className={styles.invoiceWrapper}>
      <Row className="mb-3 align-items-center">
        <Col>
          <Button variant="primary" size="sm" onClick={handleAddClick}>
            Add
          </Button>
        </Col>
        <Col className="text-end">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleShareClick}
            disabled={isSharing}
          >
            {isSharing ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Linking...
              </>
            ) : (
              "Link to ABHA"
            )}
          </Button>
        </Col>
      </Row>

      <Table
        bordered
        hover
        size="sm"
        className={`${styles.invoiceTable} ${
          config.compactTable ? styles.compactTable : ""
        }`}
      >
        <thead>
          <tr>
            <th>S.No.</th>
            {tableColumns.map((column, index) => (
              <th key={`${column.key || column.label || "column"}-${index}`}>
                {column.label}
              </th>
            ))}
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {externalLoading && mergedRows.length === 0 ? (
            <tr>
              <td colSpan={tableColumnCount} className="text-center">
                Loading records...
              </td>
            </tr>
          ) : mergedRows.length > 0 ? (
            mergedRows.map((item, index) => (
              <tr key={item._id || index}>
                <td>{index + 1}</td>
                {tableColumns.map((column, columnIndex) => {
                  const helpers = {
                    formatDate,
                    getServiceNameById,
                    getDoctorNameById,
                  };
                  const rawValue = column?.key ? item?.[column.key] : undefined;
                  const rendered =
                    typeof column.render === "function"
                      ? column.render(item, helpers)
                      : rawValue;
                  const value = rendered === 0 || rendered ? rendered : "-";
                  const isTruncatable =
                    Number(column?.maxChars) > 0 &&
                    (typeof value === "string" || typeof value === "number");
                  const plainValue = isTruncatable ? String(value) : "";
                  const displayValue =
                    isTruncatable && plainValue.length > Number(column.maxChars)
                      ? `${plainValue.slice(0, Number(column.maxChars) - 1)}...`
                      : value;

                  return (
                    <td
                      key={`${item._id || index}-${column.key || columnIndex}`}
                      title={isTruncatable ? plainValue : undefined}
                      className={isTruncatable ? styles.compactTextCell : undefined}
                    >
                      {displayValue}
                    </td>
                  );
                })}
                <td>
                  {(() => {
                    const statusMeta = getStatusMeta(item);
                    return (
                      <span className={`${styles.statusBadge} ${statusMeta.className}`}>
                        {statusMeta.label}
                      </span>
                    );
                  })()}
                </td>
                <td>
                  <Dropdown align="end">
                    <Dropdown.Toggle
                      variant="light"
                      size="sm"
                      className={styles.actionToggle}
                    >
                      <BsThreeDots />
                    </Dropdown.Toggle>
                    <Dropdown.Menu className={styles.dropdownMenu}>
                      {item?.__rowSource === "external" ? (
                        <>
                          {typeof onExternalView === "function" ? (
                            <Dropdown.Item onClick={() => onExternalView(item)}>
                              <FiEye className="me-2" />
                              View Source
                            </Dropdown.Item>
                          ) : null}
                          <Dropdown.Item onClick={() => handleImportExternalRow(item)}>
                            <FiEdit className="me-2" />
                            Import to ABHA
                          </Dropdown.Item>
                        </>
                      ) : (
                        <>
                          <Dropdown.Item
                            onClick={() => {
                              setSelectedItem(item);
                              setShowViewModal(true);
                            }}
                          >
                            <FiEye className="me-2" />
                            View
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => handleEditClick(item)}>
                            <FiEdit className="me-2" />
                            Edit
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => {
                              setReportToDelete(item._id);
                              setShowConfirmModal(true);
                            }}
                          >
                            <FiTrash2 className="me-2" />
                            Delete
                          </Dropdown.Item>
                        </>
                      )}
                    </Dropdown.Menu>
                  </Dropdown>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={tableColumnCount} className="text-center">
                No {config.title} records available.
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      <Modal show={showModal} onHide={handleModalClose} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {modalAction === "edit" ? `Edit ${config.title}` : `Add ${config.title}`}
          </Modal.Title>
        </Modal.Header>

        <Form onSubmit={handleSubmit}>
          <Modal.Body className={styles.clinicalModalBody}>
            <Form.Group className="mb-3">
              <Form.Label>
                {dateLabel} {config.requireDate ? <span className="text-danger">*</span> : null}
              </Form.Label>
              <Form.Control
                type="date"
                value={formData.consultationDate || ""}
                max={new Date().toISOString().split("T")[0]}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    consultationDate: event.target.value,
                  }))
                }
              />
            </Form.Group>

            {config.includeService ? (
              <Form.Group className="mb-3">
                <Form.Label>
                  {config.serviceLabel || "Service"}{" "}
                  {config.requireService ? <span className="text-danger">*</span> : null}
                </Form.Label>
                <Form.Select
                  value={formData.service || ""}
                  onChange={(event) =>
                    setFormData((prev) => ({
                      ...prev,
                      service: event.target.value,
                    }))
                  }
                >
                  <option value="">{servicePlaceholder}</option>
                  {(services || []).map((service) => (
                    <option key={service?._id} value={service?._id}>
                      {service?.serviceName}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            ) : null}

            {config.includeDoctor ? (
              hasAuthorModeSelector ? (
                <div className={styles.formSectionCard}>
                  <div className={styles.formSectionTitle}>
                    {config.authorSectionTitle || "Authoring Context"}
                  </div>
                  {config.authorSectionDescription ? (
                    <div className={styles.formSectionDescription}>
                      {config.authorSectionDescription}
                    </div>
                  ) : null}

                  <Form.Group className="mb-3">
                    <Form.Label>{config.authorModeLabel || "Author Source"}</Form.Label>
                    <div className={styles.authorModeGrid}>
                      {authorModeOptions.map((option) => (
                        <label
                          key={`author-mode-${option.value}`}
                          className={styles.authorModeOption}
                        >
                          <Form.Check
                            type="radio"
                            name={`author-source-${config.prefix}`}
                            checked={selectedAuthorMode === option.value}
                            onChange={() => {
                              setSelectedAuthorMode(option.value);
                              if (option.value !== doctorModeValue) {
                                setFormData((prev) => ({ ...prev, doctor: "" }));
                              }
                            }}
                          />
                          <span>{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </Form.Group>

                  {requiresDoctorByAuthorMode ? (
                    <Form.Group className="mb-1">
                      <Form.Label>
                        {config.doctorLabel || "Doctor"} <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        value={formData.doctor || ""}
                        onChange={(event) =>
                          setFormData((prev) => ({
                            ...prev,
                            doctor: event.target.value,
                          }))
                        }
                      >
                        <option value="">{doctorPlaceholder}</option>
                        {doctorOptions.map((doctor) => (
                          <option key={doctor?._id} value={doctor?._id}>
                            {doctor?.userId?.name || "Unnamed doctor"}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  ) : (
                    <div className={styles.fieldHelpText}>
                      {config.authorFallbackInfo ||
                        "Author will be set as facility (Organization) when clinician is not selected."}
                    </div>
                  )}
                </div>
              ) : (
                <Form.Group className="mb-3">
                  <Form.Label>
                    {config.doctorLabel || "Doctor"}{" "}
                    {config.requireDoctor ? <span className="text-danger">*</span> : null}
                  </Form.Label>
                  <Form.Select
                    value={formData.doctor || ""}
                    onChange={(event) =>
                      setFormData((prev) => ({
                        ...prev,
                        doctor: event.target.value,
                      }))
                    }
                  >
                    <option value="">{doctorPlaceholder}</option>
                    {doctorOptions.map((doctor) => (
                      <option key={doctor?._id} value={doctor?._id}>
                        {doctor?.userId?.name || "Unnamed doctor"}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )
            ) : null}

            {renderConfiguredFieldBlocks()}

            {config.includeMedicines ? (
              <Form.Group className="mb-3">
                <Form.Label>{config.medicinesLabel || "Medicines"}</Form.Label>
                <CreatableSelect
                  isClearable
                  isSearchable
                  options={medicineOptions}
                  value={medicineInput}
                  placeholder={
                    config.medicineNamePlaceholder || "Select or type medicine"
                  }
                  onChange={(selected) => {
                    if (!selected) return;
                    setShowError("");
                    addMedicine(selected.value || selected.label);
                  }}
                  onCreateOption={(inputValue) => {
                    setShowError("");
                    addMedicine(inputValue);
                  }}
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : null
                  }
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
                {medicineErrMsg ? (
                  <div className="text-danger mt-1">{medicineErrMsg}</div>
                ) : null}

                {(formData.medicines || []).length === 0 ? (
                  <div className="text-muted small mt-2">
                    {config.emptyMedicinesText || "No medication entries added yet."}
                  </div>
                ) : (
                  <Row className="g-3 mt-1">
                    {(formData.medicines || []).map((medicine, index) => (
                      <Col md={6} key={`med-${index}`}>
                        <div className={styles.medicineCard}>
                          <div className={styles.medicineCardHeader}>
                            <strong className={styles.medicineCardName}>
                              {medicine?.name || "Unnamed medicine"}
                            </strong>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => removeMedicineRow(index)}
                            >
                              Remove
                            </Button>
                          </div>

                          <CreatableSelect
                            isClearable
                            isSearchable
                            options={medicationInstructionOptions}
                            placeholder={
                              config.medicineInstructionPlaceholder ||
                              "Select or type instruction"
                            }
                            value={
                              medicine?.instruction
                                ? {
                                    label: medicine.instruction,
                                    value: medicine.instruction,
                                  }
                                : null
                            }
                            onChange={(selected) => {
                              handleMedicineChange(
                                index,
                                "instruction",
                                selected?.value || "",
                              );
                              setMedicineInstructionErrMsg((prev) => {
                                const next = [...prev];
                                next[index] = "";
                                return next;
                              });
                            }}
                            onCreateOption={(inputValue) => {
                              handleMedicineChange(index, "instruction", inputValue);
                              setMedicineInstructionErrMsg((prev) => {
                                const next = [...prev];
                                next[index] = "";
                                return next;
                              });
                            }}
                            menuPortalTarget={
                              typeof document !== "undefined" ? document.body : null
                            }
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                          />
                          {medicineInstructionErrMsg?.[index] ? (
                            <div className="text-danger mt-1">
                              {medicineInstructionErrMsg[index]}
                            </div>
                          ) : null}
                        </div>
                      </Col>
                    ))}
                  </Row>
                )}
              </Form.Group>
            ) : null}

            {showError && config.enableFileUpload === false ? (
              <div className="text-danger mt-1 mb-3">{showError}</div>
            ) : null}

            {config.enableFileUpload !== false ? (
              <Form.Group className="mb-3">
                <Form.Label>
                  {config.fileLabel || "Upload Report (PDF/Image)"}{" "}
                  {config.fileRequired ? <span className="text-danger">*</span> : null}
                </Form.Label>
                <Form.Control
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf"
                  multiple
                  onChange={handleFileChange}
                  ref={fileInputRef}
                />
                {fileError && <div className="text-secondary mt-1">{fileError}</div>}
                {showError && <div className="text-danger mt-1">{showError}</div>}

                {(formData.patient_report || []).map((url, index) => (
                  <div key={index} className={styles.previewBox}>
                    <button
                      type="button"
                      className={`btn-close ${styles.closeBtn}`}
                      onClick={() => {
                        const updated = (formData.patient_report || []).filter(
                          (_, itemIndex) => itemIndex !== index,
                        );
                        setFormData((prev) => ({
                          ...prev,
                          patient_report: updated,
                        }));
                      }}
                    ></button>

                    <div className={styles.previewWrapper}>
                      {/\.pdf$/i.test(url) ? (
                        <embed src={url} width="100%" height="100%" />
                      ) : (
                        <img
                          src={url}
                          className="img-fluid w-100 h-100"
                          alt="Report preview"
                        />
                      )}
                    </div>
                  </div>
                ))}

                {files.map((file, index) => (
                  <div key={index} className={styles.previewBox}>
                    <button
                      type="button"
                      className={`btn-close ${styles.closeBtn}`}
                      onClick={() =>
                        setFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== index))
                      }
                    ></button>

                    <div className={styles.previewWrapper}>
                      {file.type.startsWith("image/") ? (
                        <img
                          src={URL.createObjectURL(file)}
                          className="img-fluid w-100 h-100"
                          alt="New report preview"
                        />
                      ) : (
                        <embed
                          src={URL.createObjectURL(file)}
                          width="100%"
                          height="100%"
                        />
                      )}
                    </div>
                  </div>
                ))}
              </Form.Group>
            ) : null}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={handleModalClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading
                ? "Submitting..."
                : modalAction === "edit"
                  ? "Update"
                  : "Submit"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={showViewModal}
        onHide={() => {
          setShowViewModal(false);
          setSelectedItem(null);
        }}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{config.detailsTitle || `${config.title} Details`}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {renderDetailRow(dateLabel, formatDate(selectedItem?.consultationDate), "detail-date")}
          {showServiceColumn ? (
            renderDetailRow(
              config.tableServiceLabel || "Service",
              config.includeService
                ? getServiceNameById(selectedItem?.service)
                : selectedItem?.serviceName || "-",
              "detail-service",
            )
          ) : null}
          {showDoctorColumn ? (
            renderDetailRow(
              config.tableDoctorLabel || "Doctor",
              config.includeDoctor
                ? (getDoctorNameById(selectedItem?.doctor) === "-"
                    ? doctorFallbackLabel
                    : getDoctorNameById(selectedItem?.doctor))
                : selectedItem?.doctorName || "-",
              "detail-doctor",
            )
          ) : null}

          {Array.isArray(config.viewSections) && config.viewSections.length > 0 ? (
            config.viewSections.map((section, sectionIndex) => {
              const sectionFields = (section.fieldKeys || [])
                .map((fieldKey) => fieldConfigMap.get(fieldKey))
                .filter(Boolean);
              if (sectionFields.length === 0) return null;

              return (
                <div
                  className={styles.viewSectionBlock}
                  key={`view-section-${section.title || sectionIndex}`}
                >
                  <div className={styles.viewSectionTitle}>{section.title || "Section"}</div>
                  {section.description ? (
                    <div className={styles.fieldHelpText}>{section.description}</div>
                  ) : null}
                  {sectionFields.map((field) => (
                    renderDetailRow(
                      field.label,
                      selectedItem?.[field.key],
                      `view-${sectionIndex}-${field.key}`,
                    )
                  ))}
                </div>
              );
            })
          ) : (
            (config.fields || []).map((field) =>
              renderDetailRow(field.label, selectedItem?.[field.key], `view-${field.key}`),
            )
          )}

          {config.includeMedicines && Array.isArray(selectedItem?.medicines) ? (
            <div className="mb-3">
              <strong>{config.medicinesLabel || "Medicines"}:</strong>
              {selectedItem.medicines.length === 0 ? (
                <div className="text-muted">
                  {config.emptyMedicinesViewText ||
                    "No medication entries were documented."}
                </div>
              ) : (
                <ul className="mt-2 mb-0">
                  {selectedItem.medicines.map((medicine, index) => (
                    <li key={`view-med-${index}`}>
                      {medicine?.name || "Unnamed medication"}:{" "}
                      {medicine?.instruction || "No dosage instruction documented"}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}

          {Array.isArray(selectedItem?.patient_report) &&
          selectedItem.patient_report.length > 0 ? (
            selectedItem.patient_report.map((fileUrl, index) => {
              const isImage = /\.(jpeg|jpg|png)$/i.test(fileUrl);
              const isPdf = /\.pdf$/i.test(fileUrl);

              return (
                <div key={index} className={styles.reportContainer}>
                  {isImage && (
                    <img
                      src={fileUrl}
                      alt={`Report ${index + 1}`}
                      className={styles.reportImage}
                    />
                  )}

                  {isPdf && (
                    <iframe
                      src={fileUrl}
                      title={`Report PDF ${index + 1}`}
                      className={styles.reportPdf}
                    ></iframe>
                  )}
                </div>
              );
            })
          ) : (
            <p>{config.emptyFilesText || "No attachment files available."}</p>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowViewModal(false);
              setSelectedItem(null);
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this report?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              handleDeleteReport(reportToDelete);
              setShowConfirmModal(false);
            }}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showShareModal}
        onHide={() => setShowShareModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton className={styles.shareModalHeader}>
          <Modal.Title>Link {config.title} Records</Modal.Title>
        </Modal.Header>

        <Modal.Body className={styles.shareModalBody}>
          {reports.length > 0 ? (
            <Table
              bordered
              hover
              responsive
              size="sm"
              className={`align-middle text-center ${styles.shareTable}`}
            >
              <thead>
                <tr>
                  <th>
                    <Form.Check
                      type="checkbox"
                      id={`selectAll-${config.prefix}`}
                      onChange={handleSelectAll}
                      checked={
                        reports.filter(
                          (item) => !["Shared", "Transferred"].includes(item.status),
                        ).length > 0 &&
                        reports
                          .filter(
                            (item) => !["Shared", "Transferred"].includes(item.status),
                          )
                          .every((item) => selectedReports.includes(item._id))
                      }
                    />
                  </th>
                  <th>Consultation Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((item) => {
                  const isSelected = selectedReports.includes(item._id);
                  const isDisabled = ["Shared", "Transferred"].includes(item.status);

                  return (
                    <tr
                      key={item._id}
                      className={
                        isDisabled ? styles.shared : isSelected ? styles.selected : ""
                      }
                    >
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={isSelected || isDisabled}
                          disabled={isDisabled}
                          onChange={() => handleCheckboxChange(item._id, isDisabled)}
                        />
                      </td>
                      <td>{formatDate(item.consultationDate)}</td>
                      <td>
                        <span
                          className="badge px-3 py-2"
                          style={{
                            backgroundColor:
                              item.status === "Shared"
                                ? "#12d6ac"
                                : item.status === "Transferred"
                                  ? "#28a745"
                                  : "#3898cc",
                            color: "#fff",
                            fontWeight: "bold",
                          }}
                        >
                          {item.status === "Shared"
                            ? "Linked"
                            : item.status === "Transferred"
                              ? "Transferred"
                              : "Not Linked"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          ) : (
            <p className="text-muted">No reports available.</p>
          )}
        </Modal.Body>

        <Modal.Footer className={styles.shareModalFooter}>
          <Button variant="outline-secondary" onClick={() => setShowShareModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={
              !selectedReports.some((id) => {
                const item = reports.find((report) => report._id === id);
                return item && !["Shared", "Transferred"].includes(item.status);
              }) || isFinalSharing
            }
            onClick={handleFinalShare}
          >
            {isFinalSharing ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Linking...
              </>
            ) : (
              "Link"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showInvalidTokenModal}
        onHide={() => setShowInvalidTokenModal(false)}
        size="md"
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-semibold text-primary">Notice</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <p className="mb-0 text-muted">
            Unable to generate a link Token at the moment.
            <br />
            Please try again later.
          </p>
        </Modal.Body>
        <Modal.Footer className="border-0 d-flex justify-content-center">
          <Button variant="primary" onClick={() => setShowInvalidTokenModal(false)}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showLinkedSuccessModal}
        onHide={() => setShowLinkedSuccessModal(false)}
        size="md"
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold text-success d-flex align-items-center">
            <BsCheckCircle className="me-2" size={24} />
            Linked Successfully!
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <p className="mb-0" style={{ fontSize: "1rem", lineHeight: "1.5" }}>
            Your health data has been successfully linked with the ABHA address.
            <br />
            You can now access and manage it seamlessly in your ABHA account.
          </p>
        </Modal.Body>
        <Modal.Footer className="border-0 d-flex justify-content-center">
          <Button
            variant="success"
            onClick={() => setShowLinkedSuccessModal(false)}
            style={{ minWidth: "100px" }}
          >
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showLinkedErrorModal}
        onHide={() => setShowLinkedErrorModal(false)}
        size="md"
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton className="border-0 pb-0"></Modal.Header>
        <Modal.Body className="text-center">
          <p className="mb-0">Something went wrong! Please try again later.</p>
        </Modal.Body>
        <Modal.Footer className="border-0 d-flex justify-content-center">
          <Button variant="primary" onClick={() => setShowLinkedErrorModal(false)}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ClinicalReportSection;
